import { CommonModule } from '@angular/common';
import { Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MaterialModule } from '../../material.module';
import { FormField } from '../../models/dynamic.model';
import { FormControlValidatorsService } from '../../services/form-control-validators.service';
import { profileInfoFormFieldConfig } from '../../config/common.config';
import { FormGroup } from '@angular/forms';
import {
  measurementOptions,
  ModalPopupMessages,
  StatusCodes,
} from '../../utility/constants';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { MatDialogRef } from '@angular/material/dialog';
import { CommonService } from '../../services/common.service';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SnakbarService } from '../../services/snakbar.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-my-profile-popup',
  standalone: true,
  imports: [CommonModule, MaterialModule, SvgIconComponent],
  templateUrl: './my-profile-popup.component.html',
  styleUrl: './my-profile-popup.component.scss',
})
export class MyProfilePopupComponent {
  public profileForm!: FormGroup;
  public profileErrorMessages: any;
  public profileInfoFormFieldConfig!: FormField[];
  public measurementOptionsList = measurementOptions;
  public showSaveButton = false;
  public avatar!: string;
  public userDetails!: any;

  constructor(
    private readonly commonService: CommonService,
    private readonly formService: FormControlValidatorsService,
    private readonly ref: MatDialogRef<MyProfilePopupComponent>,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly snakbarService: SnakbarService,
    private readonly authService: AuthService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.createProfileForm();
    this.patchProfileFormValues();
  }

  public getErrorMessage(controlName: string): string {
    return this.formService.getErrorMessage(
      this.profileForm,
      this.profileErrorMessages,
      controlName
    );
  }

  public onClose() {
    this.ref.close();
  }

  public onEdit() {
    this.profileForm.enable();
    this.profileForm.get('email')?.disable();
    this.showSaveButton = true;
  }

  public onSave() {
    const fullName = this.profileForm.value?.fullName || '';
    const reqPayload = {
      id: this.userDetails.id,
      fullName: fullName.trim(),
      email: this.userDetails.email,
      measurementOptions: this.profileForm.value?.measurementOptions,
      accountId: this.userDetails.accountId,
      isCustomer: true,
    };
    this.commonService
      .updateUserDetails(reqPayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode == StatusCodes.Success) {
            this.profileUpdateSuccess(data.data, data.message);
          } else {
            this.errorHandlerService.handleApiError(data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private createProfileForm() {
    const { form, errorMessages } = this.formService.generateForm(
      profileInfoFormFieldConfig,
      this.destroyRef,
    );
    this.profileForm = form;
    this.profileErrorMessages = errorMessages;
    this.profileInfoFormFieldConfig = profileInfoFormFieldConfig.fields;
  }

  private patchProfileFormValues() {
    this.userDetails = JSON.parse(localStorage.getItem('userDetails') ?? 'null');
    this.avatar =
      this.userDetails.firstName.charAt(0) +
      this.userDetails.lastName.charAt(0);
    if (this.userDetails) {
      this.profileForm.patchValue(this.userDetails);
      this.profileForm.disable();
    }
  }

  private profileUpdateSuccess(userDetailsData: any, message: string) {
    if (userDetailsData.fullName) {
      this.userDetails.fullName = userDetailsData.fullName;
      const nameParts = userDetailsData.fullName.trim().split(/\s+/);
      this.userDetails.firstName = nameParts[0] || '';
      this.userDetails.lastName = nameParts.slice(1).join(' ') || '';
    } else {
      this.userDetails.firstName = userDetailsData.firstName;
      this.userDetails.lastName = userDetailsData.lastName;
      this.userDetails.fullName =
        `${userDetailsData.firstName} ${userDetailsData.lastName}`.trim();
    }
    this.userDetails.measurementOptions = userDetailsData.measurementOptions;
    localStorage.setItem('userDetails', JSON.stringify(this.userDetails));
    this.authService.userDetailsChanged(true);
    this.snakbarService.openCustomisedSnackBar(
      message,
      ModalPopupMessages.success
    );
    this.onClose();
  }
}
